<template>
    <div class="page-container">
        <b-navbar toggleable="lg" type="dark" variant="primary">
            <b-navbar-brand>
                <img :src="loginImage" alt="Logo" class="img-fluid" style="width: 50px; height: 50px" />
            </b-navbar-brand>
            <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
            <b-collapse id="nav-collapse" is-nav>
                <b-navbar-nav>
                    <b-form-select v-model="selectedStudent" class="mr-3 select-box" style="width: 250px;">
                        <b-form-select-option :value="null" disabled>Select Student</b-form-select-option>
                        <b-form-select-option v-for="student in studentOptions" :key="student.value" :value="student.value">
                            {{ student.text }}
                        </b-form-select-option>
                    </b-form-select>

                    <b-form-select v-model="selectedAcademicYear" class="mr-3 select-box" style="width: 250px;">
                        <b-form-select-option :value="null" disabled>Select Academic Year</b-form-select-option>
                        <b-form-select-option v-for="year in academicYearOptions" :key="year.value" :value="year.value">
                            {{ year.text }}
                        </b-form-select-option>
                    </b-form-select>
                </b-navbar-nav>
                <b-navbar-nav class="ml-auto">
                    <b-nav-item @click="logOutParent" style="display: flex; flex-direction: column; align-items: center; font-weight: bold;">
                        <i class="fas fa-power-off fa-lg" style="color: white; font-size: 1.5rem; margin-bottom: 5px;"></i>
                        <br />
                        <span style="color: black; font-size: 14px;">Logout</span>
                    </b-nav-item>
                </b-navbar-nav>
            </b-collapse>
        </b-navbar>

        <div v-if="usersubgroup.name" class="mt-3 p-3 border rounded">
            <h6 class="font-weight-bold text-left mb-3">
                Name: {{ getSelectedStudentName }} - Class: {{ usersubgroup.name }} (Academic Year: {{ selectedAcademicYear }}) | Institute: {{ usersubgroup.institution.name }}
            </h6>

            <b-tabs v-model="activeTab" class="mt-3" lazy>
                <b-tab title="Basic Info" lazy>
                    <div class="mt-3 p-3 border rounded position-relative shadow-sm" style="background-color: #f8f9fa;">
                        <div v-if="basicInfoLoading" class="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
                            style="background: rgba(255, 255, 255, 0.7); top: 0; left: 0; z-index: 10;">
                            <b-spinner label="Loading Basic Info..."></b-spinner>
                        </div>
                        <ViewProfile
                            v-if="activeTab === 0"
                            :key="'basic-'+componentKey"
                            :from_parent_login="true"
                            :show_basic_info_only="true"
                            :institutionUserId="selectedStudent"
                            :parentSelectedSubgroupId="usersubgroup.id"
                            @loaded="onBasicInfoLoaded"
                        />
                    </div>
                </b-tab>

                <b-tab title="Daily Attendance Data" lazy>
                    <div class="mt-3 p-3 border rounded position-relative shadow-sm" style="background-color: #f8f9fa;">
                        <StudentAttendence
                            v-if="activeTab === 1"
                            :key="'attendance-'+componentKey"
                            :from_parent_login="true"
                            :parentSelectedInstitutionUserId="selectedStudent"
                            :parentSelectedAcademicYear="selectedAcademicYear"
                        />
                    </div>
                </b-tab>

                <b-tab title="Consolidate Performance Statement" lazy>
                    <div class="mt-3 p-3 border rounded position-relative shadow-sm" style="background-color: #f8f9fa;">
                        <ViewProfile
                            v-if="activeTab === 2"
                            :key="'performance-'+componentKey"
                            :from_parent_login="true"
                            :show_basic_info_only="false"
                            :institutionUserId="selectedStudent"
                            :parentSelectedSubgroupId="usersubgroup.id"
                        />
                    </div>
                </b-tab>
            </b-tabs>
        </div>

        <div v-else class="mt-3 p-3 border rounded text-danger text-center">
            <p>Student not found. Please check your selections.</p>
        </div>
    </div>
</template>

<script>
import loginImage from '@/assets/images/login1.png';
import StudentAttendence from '@/components/templates/Dashboard/attendence/StudentAttendence.vue';
import ViewProfile from '@/components/templates/Dashboard/Institute/usergroups/usersubgroups/subgroup_users_edit/ViewProfile.vue';

export default {
    components: { StudentAttendence, ViewProfile },
    data() {
        return {
            loginImage,
            selectedStudent: null,
            selectedAcademicYear: null,
            studentOptions: [],
            usersubgroup: {},
            basicInfoLoading: false,
            activeTab: 0,
            componentKey: 0,
        };
    },
    computed: {
        getSelectedStudentName() {
            return this.studentOptions.find(s => s.value === this.selectedStudent)?.text || "";
        },
        academicYearOptions() {
            const student = this.studentOptions.find(s => s.value === this.selectedStudent);
            if (!student || !student.usersubgroups) return [];

            const seen = new Set();
            return student.usersubgroups
                .map(subgroup => {
                    const value = `${subgroup.academic_year}-${subgroup.academic_semester}`;
                    const text = `${subgroup.academic_year} ${subgroup.academic_semester}`;
                    return { value, text };
                })
                .filter(option => {
                    if (seen.has(option.value)) return false;
                    seen.add(option.value);
                    return true;
                });
        }
    },
    async created() {
        await this.getParentLoginInitialDatas();
    },
    watch: {
        selectedStudent: 'triggerSearch',
        selectedAcademicYear: 'triggerSearch',
    },
    methods: {
        async getParentLoginInitialDatas() {
            try {
                const { data } = await this.$axios.get(`${this.$store.getters.getAPIKey.mainAPI}/parentuser/student-details`);
                this.studentOptions = data.map(student => ({
                    value: student.id,
                    text: `${student.first_name} ${student.last_name}`,
                    institution_id: student.institution_id,
                    usersubgroups: student.usersubgroups.sort((a, b) => {
                        const yearA = parseInt(a.academic_year.split('-')[0], 10);
                        const yearB = parseInt(b.academic_year.split('-')[0], 10);
                        return yearA !== yearB ? yearA - yearB : a.academic_semester === "odd" ? -1 : 1;
                    })
                }));

                if (this.studentOptions.length > 0) {
                    this.selectedStudent = this.studentOptions[0].value;
                    const firstStudent = this.studentOptions[0];
                    const academicOptions = firstStudent.usersubgroups.map(subgroup => ({
                        value: `${subgroup.academic_year}-${subgroup.academic_semester}`,
                        text: `${subgroup.academic_year} ${subgroup.academic_semester}`
                    }));

                    const seen = new Set();
                    const uniqueAcademicOptions = academicOptions.filter(option => {
                        if (seen.has(option.value)) return false;
                        seen.add(option.value);
                        return true;
                    });

                    if (uniqueAcademicOptions.length > 0) {
                        this.selectedAcademicYear = uniqueAcademicOptions[0].value;
                    }
                }
            } catch (error) {
                console.error("Failed to fetch initial data:", error);
            }
        },
        async triggerSearch() {
            if (!this.selectedStudent || !this.selectedAcademicYear) {
                this.basicInfoLoading = false;
                return;
            }

            try {
                const [year, semester] = this.selectedAcademicYear.split(/-(?=[a-zA-Z]+)/);
                this.basicInfoLoading = true;

                const { data } = await this.$axios.post(
                    `${this.$store.getters.getAPIKey.mainAPI}/parentuser/usersubgroups/student-subgroup-details`,
                    {
                        institution_user_id: this.selectedStudent,
                        selected_year: year,
                        selected_semester: semester,
                    }
                );

                this.usersubgroup = data;
                this.componentKey++; // 🔁 Force component refresh

                setTimeout(() => {
                    this.basicInfoLoading = false;
                }, 5000);
            } catch (error) {
                console.error('Search error:', error);
                this.basicInfoLoading = false;
            }
        },
        onBasicInfoLoaded() {
            this.basicInfoLoading = false;
        },
        logOutParent() {
            const keysToRemove = [
                "publicexamAuthToken", "parentAuthToken", "publicUrlUser", "token", "userPermissions", "auditorAuthToken"
            ];
            keysToRemove.forEach(key => localStorage.removeItem(key));
            this.$router.push('/parentuser/login');
        }
    }
};
</script>

<style scoped>
.select-box {
    width: 250px;
}
.page-container {
    background-color: white;
    min-height: 100vh;
}
.b-navbar {
    background-color: white !important;
}
</style>
